import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CACHE_KEY_LOCATIONS } from "../../../utils/constants";
import { LocationInterface } from "../../../models/TicketModule/Location";
import locationService from "../../../services/ticketService/locationService";

interface DeleteLocationContext {
  previousLocations: LocationInterface[];
  deletedLocation: LocationInterface | undefined;
}

const useDeleteLocations = (page: number, pageSize: number) => {
  const queryClient = useQueryClient();

  return useMutation<
    void, // No return data after deletion
    Error,
    string, // ID of the location to delete
    DeleteLocationContext
  >({
    mutationFn: locationService.Remove, // Remove should take a string ID

    onMutate: (locationId: string) => {
      const previousLocations =
        queryClient.getQueryData<LocationInterface[]>([
          CACHE_KEY_LOCATIONS,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ]) || [];

      // Optimistically update the cache
      queryClient.setQueryData<LocationInterface[]>(
        [
          CACHE_KEY_LOCATIONS,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        previousLocations.filter((location) => location.id !== locationId) // Use locationId to filter
      );

      return {
        previousLocations,
        deletedLocation: previousLocations.find(
          (location) => location.id === locationId
        ),
      };
    },

    onError: (error, locationId, context) => {
      if (!context) return;

      // Rollback to the previous state in case of error
      queryClient.setQueryData<LocationInterface[]>(
        [
          CACHE_KEY_LOCATIONS,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        context.previousLocations
      );
    },

    onSuccess: () => {
      // Optionally, you could perform additional actions on success
      // For example, invalidating queries or updating other parts of the state
    },
  });
};

export default useDeleteLocations;
